import { computed, Ref, ref, markRaw } from 'vue';
import { defineStore } from 'pinia';
import {
  BasicCollection,
  ContentCollection,
  ShareableContentCollection,
} from '@ilteducation/content-collections-types';
import { AxiosError, AxiosResponse } from 'axios';
import { getContentCollection } from './api';
import useCollectionContentsStore from './collection-contents';
import { isFullProjection } from './utils';
import { useMyProfiles } from '@ilteducation/profile';

const useContentCollectionsStore = defineStore('content-collections', () => {
  type StoreCollection = ContentCollection | BasicCollection | ShareableContentCollection | undefined | null;

  const collections = ref<Record<string, StoreCollection>>({});
  const isLoadingFlags = ref<Record<string, boolean | undefined>>({});
  const errors = ref<Record<string, AxiosResponse>>({});
  const selectedContents = ref<string[]>([]);

  const { current: currentProfile } = useMyProfiles();

  const setLoadingById = (collectionId: string, isLoading: boolean) => {
    isLoadingFlags.value[collectionId] = isLoading;
  };

  const setErrorsById = (collectionId: string, err: AxiosResponse) => {
    errors.value[collectionId] = err;
  };

  /**
   * `reloadContents: false` is for callers that already keep the loaded contents in step themselves
   * (reordering does), so the list is not thrown away and refetched behind the user.
   */
  const put = (newCollections: (ShareableContentCollection | BasicCollection)[], reloadContents = true) => {
    newCollections.forEach((newCollection) => {
      const existingCollection = collections.value[newCollection.id];
      if (existingCollection && isFullProjection(existingCollection) && !isFullProjection(newCollection)) {
        // do not overwrite, in case of "full" model already being in store but the new is only a basic projection
        return;
      }
      if (
        reloadContents &&
        existingCollection &&
        isFullProjection(existingCollection) &&
        isFullProjection(newCollection)
      ) {
        // assume contents was update, trigger reload
        const { resetById } = useCollectionContentsStore();
        resetById(newCollection.id); // TODO instead of this hack, we should perhaps retrieve the content object and add to collection-contents?
      }

      collections.value[newCollection.id] = markRaw(newCollection);
    });
  };

  const load = async (collectionId: string) => {
    setLoadingById(collectionId, true);
    try {
      const collection = await getContentCollection(collectionId, currentProfile.value?.id);
      if (collection) {
        const newCollection = { ...collection, id: collectionId };
        collections.value[collectionId] = markRaw(newCollection);
      } else {
        collections.value[collectionId] = collection; // Handles null case
      }
    } catch (err) {
      if ((err as AxiosError).response) {
        setErrorsById(collectionId, (err as AxiosError).response!);
      }
    } finally {
      setLoadingById(collectionId, false);
    }
  };

  const isLoadingById = computed(
    () => (collectionId: Ref<string>) => isLoadingFlags.value[collectionId.value] ?? false,
  );
  const collectionById = computed(() => (collectionId: Ref<string>) => collections.value[collectionId.value]);
  const collectionsByIds = computed(() => (collectionIds: Ref<string[]>) => {
    return collectionIds.value.map((id) => collections.value[id]);
  });
  const errorById = computed(() => (collectionId: Ref<string>) => errors.value[collectionId.value]);

  return {
    collections: computed(() => collections.value),
    isLoadingById,
    setLoadingById,
    collectionById,
    collectionsByIds,
    errorById,
    put,
    load,
    selectedContents,
  };
});

export default useContentCollectionsStore;
